/**
 * Trading Slice для управления торговыми данными
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { Order } from "entities/order";
import type {
  ExpirationBounds,
  ExpirationLimits,
  TradingPair,
} from "shared/model";
import { clampDuration } from "shared/utils";
import { logout } from "./authSlice";

interface PriceData {
  symbol: string;
  price: number;
  timestamp: number;
  change: number;
  changePercent: number;
}

export interface ChartDataPoint {
  timestamp: number;
  price?: number; // Для линейного графика
  volume?: number;
  // OHLC данные для свечей и баров
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  chart_type?: string; // Тип графика: 'line', 'candles', 'bars'
}

// Интерфейс для фиксированных точек по таймфрейму
interface ChartPoint {
  timestamp: number;
  price: number;
  // OHLC данные для свечей и баров
  open?: number;
  high?: number;
  low?: number;
  close?: number;
}

// Добавляем интерфейс для текущих точек периодов с поддержкой OHLC
interface CurrentPeriodPoint {
  timestamp: number;
  price: number; // Текущая цена (для линейного графика)
  endTime: number; // время окончания текущего периода
  // OHLC данные для текущего периода
  open?: number; // Цена открытия периода
  high?: number; // Максимальная цена периода
  low?: number; // Минимальная цена периода
  close?: number; // Текущая цена (будет последней ценой периода)
  isNewPeriod?: boolean; // Флаг начала нового периода
}

interface Balance {
  demo: number;
  real: number;
}

interface Settings {
  demo_balance: boolean;
  real_balance: boolean;
  sound_enabled: boolean;
  notifications_enabled: boolean;
  timeframe: string;
  // true = сервер вернул сохранённое предпочтение, не переопределять автоматически
  accountPreferenceSaved: boolean;
}

export interface TradingState {
  // Валютные пары
  currencyPairs: TradingPair[];
  selectedPair: string | null;
  // Лимиты экспирации выбранной пары; пресеты — только из последнего t=10.
  expirationLimits: ExpirationLimits;

  // Тип аккаунта
  accountType: "demo" | "real";

  // Цены
  prices: { [symbol: string]: PriceData };

  // Ордера
  orders: Order[];
  orderHistory: Order[];

  // Баланс
  balance: Balance;

  // Настройки
  settings: Settings;

  orderDuration: number;

  // Режим Switch Time на поле Time: линия экспирации показывает часы:минуты
  // и двигается только при смене минуты, а не каждую секунду.
  switchTimeEnabled: boolean;

  // Исторические данные
  historyData: { [symbol: string]: any[] };

  // Данные для графика с поддержкой типа графика
  chartData: { [symbol: string]: ChartDataPoint[] };
  chartDataTypes: { [symbol: string]: string }; // Тип графика для каждого символа

  // Фиксированные точки по таймфрейму
  chartFixedPoints: Record<string, ChartPoint[]>;

  // Текущие точки периодов (обновляются при каждом сообщении типа 14)
  currentPeriodPoints: Record<string, CurrentPeriodPoint>;

  // Выбранный таймфрейм для графика
  selectedTimeframe: string;

  // Состояние наведения на кнопки торговли
  tradeButtonHover: "buy" | "sell" | null;

  // Состояние загрузки
  loading: {
    currencyPairs: boolean;
    orders: boolean;
    history: boolean;
  };

  error: string | null;
  // Если сервер отвечает rate-limit, здесь хранится client-time (ms) до которого запрещены запросы
  rateLimitUntil?: number | null;
}

const initialState: TradingState = {
  currencyPairs: [],
  selectedPair: null,
  // Fallback до первого t=10: кнопок пресетов нет, пока их не прислал сервер.
  expirationLimits: {
    min_expiration_sec: 60,
    max_expiration_sec: 0,
    expiration_step_sec: 30,
    expiration_presets_sec: [],
  },
  accountType: "demo",
  prices: {},
  orders: [],
  orderHistory: [],
  balance: { demo: 10000, real: 0 },
  settings: {
    demo_balance: true,
    real_balance: false,
    sound_enabled: true,
    notifications_enabled: true,
    timeframe: "30S",
    accountPreferenceSaved: false,
  },
  orderDuration: 60,
  switchTimeEnabled: false,
  historyData: {},
  chartData: {},
  chartDataTypes: {},
  chartFixedPoints: {},
  currentPeriodPoints: {},
  selectedTimeframe: "30S",
  tradeButtonHover: null,
  loading: {
    currencyPairs: false,
    orders: false,
    history: false,
  },
  error: null,
  rateLimitUntil: null,
};

const TIMEFRAME_TO_MS: Record<string, number> = {
  "5S": 5_000,
  "10S": 10_000,
  "15S": 15_000,
  "30S": 30_000,
  "45S": 45_000,
  "1M": 60_000,
  "3M": 180_000,
  "5M": 300_000,
  "15M": 900_000,
  "30M": 1_800_000,
  "1H": 3_600_000,
  "4H": 14_400_000,
  "1D": 86_400_000,
};

const getTimeframeMs = (timeframe?: string | null) => {
  if (!timeframe) return 60_000;
  const normalized = timeframe.toUpperCase();
  return TIMEFRAME_TO_MS[normalized] ?? 60_000;
};

/**
 * Normalize timestamps coming from server or client to milliseconds since epoch.
 * Server may send seconds (typical for many APIs) or milliseconds.
 */
function normalizeToMs(ts: number | undefined | null): number {
  if (!ts) return Date.now();
  // If value looks like milliseconds ( > 1e12 ) keep as is, otherwise treat as seconds and convert
  return ts > 1e12 ? ts : ts * 1000;
}

export const selectIsCurrentPairMarketOpen = (state: TradingState): boolean => {
  const pair = state.currencyPairs.find((p) => p.symbol === state.selectedPair);
  return pair?.isMarketOpen ?? true;
};

export const selectCurrentPairWinPercent = (state: TradingState): number => {
  const pair = state.currencyPairs.find((p) => p.symbol === state.selectedPair);
  return pair?.winPayoutPercent ?? 0;
};

const tradingSlice = createSlice({
  name: "trading",
  initialState,
  reducers: {
    // Валютные пары
    setTradingPairs: (state, action: PayloadAction<TradingPair[]>) => {
      state.currencyPairs = action.payload;
      state.loading.currencyPairs = false;
    },

    updateMarketOpenStatus: (
      state,
      action: PayloadAction<Record<string, boolean>>,
    ) => {
      for (const pair of state.currencyPairs) {
        if (pair.symbol in action.payload) {
          pair.isMarketOpen = action.payload[pair.symbol];
        }
      }
    },

    setSelectedPair: (state, action: PayloadAction<string>) => {
      state.selectedPair = action.payload;
    },

    // t=10: смена пары — новые лимиты и пресеты; длительность подтягиваем
    // под них (было 30с, у новой пары min 60 → 60).
    setExpirationLimits: (state, action: PayloadAction<ExpirationLimits>) => {
      state.expirationLimits = action.payload;
      state.orderDuration = clampDuration(state.orderDuration, action.payload);
    },

    // t=8: tier/payout обновился — меняем только min/max, пресеты остаются
    // от последнего t=10 до следующей смены пары.
    setExpirationBounds: (state, action: PayloadAction<ExpirationBounds>) => {
      const { min_expiration_sec, max_expiration_sec } = action.payload;
      Object.assign(state.expirationLimits, {
        min_expiration_sec,
        max_expiration_sec,
      });
      state.orderDuration = clampDuration(
        state.orderDuration,
        state.expirationLimits,
      );
    },

    // Тип аккаунта
    setAccountType: (state, action: PayloadAction<"demo" | "real">) => {
      state.accountType = action.payload;
    },

    // Цены
    updatePrice: (state, action: PayloadAction<PriceData>) => {
      state.prices[action.payload.symbol] = action.payload;
    },

    // Ордера
    addOrder: (state, action: PayloadAction<Order>) => {
      state.orders.push(action.payload);
    },

    updateOrder: (
      state,
      action: PayloadAction<Partial<Order> & { id: string }>,
    ) => {
      const index = state.orders.findIndex(
        (order) => order.id === action.payload.id,
      );
      if (index !== -1) {
        const { openPrice, timestamp, ...rest } = action.payload;
        state.orders[index] = { ...state.orders[index], ...rest };
      }
    },

    closeOrder: (
      state,
      action: PayloadAction<{
        id: string;
        closePrice: number;
        profit: number;
        closeTimestamp?: number;
      }>,
    ) => {
      const index = state.orders.findIndex(
        (order) => order.id === action.payload.id,
      );
      if (index !== -1) {
        const order = state.orders[index];
        order.status = "closed";
        order.closePrice = action.payload.closePrice;
        order.profit = action.payload.profit;

        // Prefer server-provided timestamp if available (and normalize it to ms),
        // otherwise fall back to local time. This avoids timezone/seconds-vs-ms mismatches.
        const tsMs = action.payload.closeTimestamp
          ? normalizeToMs(action.payload.closeTimestamp)
          : Date.now();
        order.closeTimestamp = tsMs;

        // Перемещаем в историю
        state.orderHistory.unshift(order);
        state.orders.splice(index, 1);

        // ВАЖНО: Баланс НЕ меняем локально.
        // Баланс обновляем только по серверному сообщению t=17 (update_balance),
        // иначе возможен двойной учёт (локально + сервер).
        // if (order.demo_balance) {
        //   state.balance.demo += action.payload.profit;
        // }
        // if (order.real_balance) {
        //   state.balance.real += action.payload.profit;
        // }
      }
    },

    setOrderHistory: (state, action: PayloadAction<Order[]>) => {
      // Фильтруем: оставляем только закрытые сделки, закрытые не более 5 секунд назад
      // const now = Date.now();
      // state.orderHistory = action.payload.filter(order => {
      //   if (order.status !== 'closed') return false;
      //   if (!order.closeTimestamp) return true;
      //   return now - normalizeToMs(order.closeTimestamp) <= 5000;
      // });
      state.orderHistory = action.payload;
      state.loading.orders = false;
    },

    // Удаление ордера из истории по id
    removeOrderFromHistory: (state, action: PayloadAction<string>) => {
      state.orderHistory = state.orderHistory.filter(
        (order) => order.id !== action.payload,
      );
    },

    // Баланс
    updateBalance: (state, action: PayloadAction<Balance>) => {
      state.balance = action.payload;
    },

    // Настройки
    updateSettings: (state, action: PayloadAction<Partial<Settings>>) => {
      state.settings = { ...state.settings, ...action.payload };
    },

    setOrderDuration: (state, action: PayloadAction<number>) => {
      state.orderDuration = action.payload;
    },

    setSwitchTimeEnabled: (state, action: PayloadAction<boolean>) => {
      state.switchTimeEnabled = action.payload;
    },

    // Исторические данные
    setHistoryData: (
      state,
      action: PayloadAction<{ symbol: string; data: any[] }>,
    ) => {
      state.historyData[action.payload.symbol] = action.payload.data;
      state.loading.history = false;
    },

    // Данные для графика
    setChartData: (
      state,
      action: PayloadAction<{
        symbol: string;
        data: ChartDataPoint[];
        chartType?: string;
      }>,
    ) => {
      state.chartData[action.payload.symbol] = action.payload.data;
      if (action.payload.chartType) {
        state.chartDataTypes[action.payload.symbol] = action.payload.chartType;
      }
    },

    addChartDataPoint: (
      state,
      action: PayloadAction<{ symbol: string; dataPoint: ChartDataPoint }>,
    ) => {
      if (!state.chartData[action.payload.symbol]) {
        state.chartData[action.payload.symbol] = [];
      }

      const chartData = state.chartData[action.payload.symbol];
      chartData.push(action.payload.dataPoint);

      // Ограничиваем количество точек (например, последние 100)
      if (chartData.length > 100) {
        chartData.shift();
      }
    },

    // Добавление фиксированной точки по таймфрейму с поддержкой OHLC
    addFixedPoint: (
      state,
      action: PayloadAction<{ symbol: string; point: ChartPoint }>,
    ) => {
      const { symbol, point } = action.payload;
      if (!state.chartFixedPoints[symbol]) {
        state.chartFixedPoints[symbol] = [];
      }

      // Проверяем, не существует ли уже точка с таким же timestamp
      const existingPointIndex = state.chartFixedPoints[symbol].findIndex(
        (p) => p.timestamp === point.timestamp,
      );

      if (existingPointIndex === -1) {
        // Добавляем новую точку
        state.chartFixedPoints[symbol].push(point);
      } else {
        // Обновляем, не позволяя low/high “улучшаться” для закрытой свечи
        const existing = state.chartFixedPoints[symbol][existingPointIndex];
        const safeExistingLow = existing.low ?? point.low ?? point.price;
        const safeExistingHigh = existing.high ?? point.high ?? point.price;
        state.chartFixedPoints[symbol][existingPointIndex] = {
          ...existing,
          open: existing.open ?? point.open,
          close: point.close,
          low: Math.min(safeExistingLow, point.low ?? safeExistingLow),
          high: Math.max(safeExistingHigh, point.high ?? safeExistingHigh),
        };
      }

      // Сортируем точки по времени
      state.chartFixedPoints[symbol].sort((a, b) => a.timestamp - b.timestamp);

      // Ограничиваем количество точек (например, последние 500)
      if (state.chartFixedPoints[symbol].length > 500) {
        state.chartFixedPoints[symbol].shift();
      }
    },

    // Обновление OHLC данных текущего периода
    updateCurrentOHLC: (
      state,
      action: PayloadAction<{
        symbol: string;
        price: number;
        periodEndTime: number;
      }>,
    ) => {
      const { symbol, price, periodEndTime } = action.payload;

      const currentPoint = state.currentPeriodPoints[symbol];
      const timeframeMs = getTimeframeMs(state.selectedTimeframe);
      const periodStartTime = Math.max(0, periodEndTime - timeframeMs);
      const normalizedStart = Math.floor(periodStartTime / 1000) * 1000;

      // Если точки нет или начался новый период
      if (!currentPoint || periodEndTime !== currentPoint.endTime) {
        // Первый тик новой свечи — чистый прямоугольник без фитилей
        state.currentPeriodPoints[symbol] = {
          timestamp: normalizedStart,
          price: price,
          endTime: periodEndTime,
          open: price,
          high: price,
          low: price,
          close: price,
          isNewPeriod: true,
        };
      } else {
        // Обновляем существующую точку периода
        const point = state.currentPeriodPoints[symbol];

        // 🔧 ИСПРАВЛЕНО: Обновляем high и low ТОЛЬКО на основе реальных цен ТЕКУЩЕГО периода
        point.high = Math.max(point.high || price, price);
        point.low = Math.min(point.low || price, price);

        // Обновляем close (текущая цена всегда становится ценой закрытия)
        point.close = price;
        point.price = price; // Для совместимости с линейным графиком
        point.isNewPeriod = false;

        // 🔧 УБРАНО: Избыточная валидация которая портила логику новых свечей
        // Простая валидация: если high < low, меняем местами (но это не должно происходить)
        if (point.high < point.low) {
          const temp = point.high;
          point.high = point.low;
          point.low = temp;
        }
      }
    },

    // Обновление текущей точки периода (старый метод для совместимости)
    updateCurrentPoint: (
      state,
      action: PayloadAction<{
        symbol: string;
        price: number;
        periodEndTime: number;
      }>,
    ) => {
      const { symbol, price, periodEndTime } = action.payload;
      const timeframeMs = getTimeframeMs(state.selectedTimeframe);
      const periodStartTime = Math.max(0, periodEndTime - timeframeMs);
      const normalizedStart = Math.floor(periodStartTime / 1000) * 1000;

      // Обновляем или создаем текущую точку периода
      state.currentPeriodPoints[symbol] = {
        timestamp: normalizedStart,
        price: price,
        endTime: periodEndTime,
        open: price,
        high: price,
        low: price,
        close: price,
        isNewPeriod: true,
      };
    },

    // Состояние загрузки
    setLoading: (
      state,
      action: PayloadAction<{
        key: keyof TradingState["loading"];
        value: boolean;
      }>,
    ) => {
      state.loading[action.payload.key] = action.payload.value;
    },

    // Ошибки
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    // Установка таймфрейма
    setSelectedTimeframe: (state, action: PayloadAction<string>) => {
      // Если таймфрейм действительно изменился, очищаем данные графика
      if (state.selectedTimeframe !== action.payload) {
        // Очищаем фиксированные точки всех символов (они привязаны к предыдущему таймфрейму)
        state.chartFixedPoints = {};

        // Очищаем текущие точки периодов всех символов
        state.currentPeriodPoints = {};

        // Очищаем исторические данные графика (они будут перезапрошены с новым таймфреймом)
        state.chartData = {};
        state.chartDataTypes = {};
      }

      state.selectedTimeframe = action.payload;
    },

    // Новый action для принудительной очистки данных графика
    clearChartData: (state, action: PayloadAction<{ symbol?: string }>) => {
      const { symbol } = action.payload;

      if (symbol) {
        // Очищаем данные только для конкретного символа
        delete state.chartData[symbol];
        delete state.chartDataTypes[symbol];
        delete state.chartFixedPoints[symbol];
        delete state.currentPeriodPoints[symbol];
      } else {
        // Очищаем все данные графика
        state.chartData = {};
        state.chartDataTypes = {};
        state.chartFixedPoints = {};
        state.currentPeriodPoints = {};
      }
    },

    setTradeButtonHover: (
      state,
      action: PayloadAction<"buy" | "sell" | null>,
    ) => {
      state.tradeButtonHover = action.payload;
    },
    // Установка ограничения от сервера: время (ms, client-time) до которого следует ждать
    setRateLimit: (state, action: PayloadAction<number | null>) => {
      state.rateLimitUntil = action.payload;
    },
  },
  extraReducers: (builder) => {
    // На логауте сбрасываем весь торговый стейт (выбранная пара, цены, ордера, графики
    // и т.д.) — иначе, если следующая регистрация/логин происходит в том же SPA-сеансе
    // без полной перезагрузки страницы, старая selectedPair остаётся в сторе и гоняется
    // наперегонки с новой парой, которую сервер выбирает для свежего аккаунта.
    builder.addCase(logout, () => initialState);
  },
});

export const {
  setTradingPairs,
  updateMarketOpenStatus,
  setSelectedPair,
  setExpirationLimits,
  setExpirationBounds,
  setAccountType,
  updatePrice,
  addOrder,
  updateOrder,
  closeOrder,
  setOrderHistory,
  removeOrderFromHistory,
  updateBalance,
  updateSettings,
  setOrderDuration,
  setSwitchTimeEnabled,
  setHistoryData,
  setChartData,
  addChartDataPoint,
  addFixedPoint,
  updateCurrentOHLC,
  updateCurrentPoint,
  setLoading,
  setError,
  clearError,
  setSelectedTimeframe,
  clearChartData,
  setTradeButtonHover,
  setRateLimit,
} = tradingSlice.actions;

export default tradingSlice.reducer;
